import { useState, useMemo } from 'react';
import { Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { fetchProject, getTime } from '../../../../../../services/projects-services';
import Loader from '../../../../../../components/loader/loader';
import { useRateCardById } from '../../../../../../hooks/useRateCardById';
import { createRolesMap } from '../../../../../../utils/rateCard';
import { ProjectFinancialsStyled } from '../project-financials.styles';
import {
  getFormattedExpenseDataForProject,
  getRateForRole,
} from './single-project-financials.utils';
import { useParams } from 'react-router-dom';
import { isSomething } from '../../../../../../utils/utils';
import {
  calculateBudgetForDeliverable,
  calculateBudgetForProject,
  getRolesListForDeliverable,
} from '../../../../../planner/milestone/utils/budget.utils';
import { formatNumber } from '../../../../../../utils/utils';
import ProgressBar from '../../components/progress-bar/progress-bar';
import Accordian from '../../components/accordian/accordian';
import DeliverableProgressWithLabel from '../../components/deliverable-progress-with-label/deliverable-progress-with-label';

const SingleProjectFinancials = ({ companyData }) => {
  const [expanded, setExpanded] = useState('');

  const { id } = useParams();

  const { data: projectObj, isLoading: isProjectLoading } = useQuery({
    queryKey: ['project', id],
    queryFn: () => fetchProject(id),
  });

  const { data: rateCard = {} } = useRateCardById(projectObj?.rateOption);

  const fetchedRolesList = useMemo(() => createRolesMap(rateCard), [rateCard]);

  const { plannerType, plannerServiceValue } = projectObj || {};

  const resources = projectObj?.resources;
  const deliverables = projectObj?.deliverables;

  const { data: users, isLoading: isUsersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => getTime(),
  });

  let totalBudgetCost, totalBudgetedHours;
  if (plannerType === 'Fixed Fee Project' || plannerType === 'Fixed Fee Deliverable') {
    totalBudgetCost = plannerServiceValue;
    totalBudgetedHours = 0;
  } else {
    const projectBudget = calculateBudgetForProject(deliverables, resources);
    totalBudgetedHours = projectBudget.hours;
    totalBudgetCost = projectBudget.value;
  }

  const { data: projectRateCard = {} } = useRateCardById(projectObj?.rateOption);
  const matchedCompany = companyData?.find(
    (c) => String(c.companyID) === String(projectObj?.client),
  );
  const clientRateCardId = matchedCompany?.rateCard;

  const { data: companyRateCard = {} } = useRateCardById(clientRateCardId);

  const projectRolesMap = useMemo(() => createRolesMap(projectRateCard), [projectRateCard]);
  const companyRolesMap = useMemo(() => createRolesMap(companyRateCard), [companyRateCard]);

  const expenseData = getFormattedExpenseDataForProject(
    users,
    id,
    resources,
    projectRolesMap,
    companyRolesMap,
  );

  const expenseDataWithRoleAndDeliverable = expenseData?.filter(
    (x) => isSomething(x?.role) && (isSomething(x?.deliverableId) || isSomething(x?.deliverable)),
  );

  const normalizeComparableValue = (value) => String(value || '').trim();

  const isEntryForDeliverable = (entry, deliverable) => {
    const entryDeliverableId = normalizeComparableValue(entry?.deliverableId);
    const deliverableId = normalizeComparableValue(deliverable?.id);

    if (entryDeliverableId && deliverableId) {
      return entryDeliverableId === deliverableId;
    }

    return (
      normalizeComparableValue(entry?.deliverable) === normalizeComparableValue(deliverable?.name)
    );
  };

  const isSameEntryDeliverable = (firstEntry, secondEntry) => {
    const firstDeliverableId = normalizeComparableValue(firstEntry?.deliverableId);
    const secondDeliverableId = normalizeComparableValue(secondEntry?.deliverableId);

    if (firstDeliverableId && secondDeliverableId) {
      return firstDeliverableId === secondDeliverableId;
    }

    return (
      normalizeComparableValue(firstEntry?.deliverable) ===
      normalizeComparableValue(secondEntry?.deliverable)
    );
  };

  const otherActuals = expenseData?.filter((entry) => {
    if (!plannerType) return true;
    const isRoleBudgetedForDeliverable = deliverables?.some(
      (deliverable) =>
        isEntryForDeliverable(entry, deliverable) &&
        getRolesListForDeliverable(deliverable, resources).some(
          (budgetedRole) =>
            String(budgetedRole?.role).toLowerCase() === String(entry?.role).toLowerCase(),
        ),
    );

    return !isRoleBudgetedForDeliverable;
  });

  const otherActualsSummary = otherActuals?.reduce(
    (totals, entry) => {
      const rate = getRateForRole({
        role: entry?.role,
        user: entry?.users,
        resources,
        projectRolesMap,
        companyRolesMap,
      });

      const cost = entry?.cost || (entry?.hours || 0) * rate;
      const hours = entry?.hours || 0;

      return {
        hours: totals.hours + hours,
        cost: totals.cost + cost,
      };
    },
    { hours: 0, cost: 0 },
  );

  const otherActualsByRole = otherActuals?.reduce((acc, entry) => {
    const role =
      entry?.role ||
      entry?.users?.role ||
      'Unknown Role (not included in estimates & using default role rates)';
    const rate = getRateForRole({
      role: entry?.role,
      user: entry?.users,
      resources,
      projectRolesMap,
      companyRolesMap,
    });

    const cost = entry?.cost || (entry?.hours || 0) * rate;

    const hours = entry?.hours || 0;

    if (!acc[role]) {
      acc[role] = { hours: 0, cost: 0 };
    }

    acc[role].hours += hours;
    acc[role].cost += cost;

    return acc;
  }, {});

  const rolesList = Object.entries(otherActualsByRole).map(([role, values]) => ({
    role,
    hours: values.hours,
    cost: values.cost,
  }));
  const otherActualsBreakdown = rolesList
    .filter((role) => role.hours > 0) // Exclude roles with 0 hours
    .map((role) => ({
      roleName: role.role,
      hours: role.hours,
      cost: role.cost,
    }));

  const getMilestoneTotals = (deliverable) => {
    if (!deliverable) return { milestoneCostSpent: 0, milestoneHoursSpent: 0 };

    // pick the correct data‐set depending on plannerType
    const entries =
      plannerType === 'Time + Materials'
        ? // only budgeted roles:
          expenseDataWithRoleAndDeliverable.filter((x) => isEntryForDeliverable(x, deliverable))
        : // all tracked time for that deliverable:
          expenseData.filter((x) => isEntryForDeliverable(x, deliverable));

    // sum actuals
    const { milestoneCostSpent, milestoneHoursSpent } = entries.reduce(
      (totals, e) => ({
        milestoneCostSpent: totals.milestoneCostSpent + (e.cost || 0),
        milestoneHoursSpent: totals.milestoneHoursSpent + (e.hours || 0),
      }),
      { milestoneCostSpent: 0, milestoneHoursSpent: 0 },
    );

    return { milestoneCostSpent, milestoneHoursSpent };
  };

  // Calculate totalExpenseSummary excluding roles already in Other Actuals
  const totalExpenseSummary = expenseDataWithRoleAndDeliverable?.reduce(
    (totals, entry) => {
      const isInOtherActuals = otherActuals?.some(
        (other) =>
          other.flowId === entry.flowId &&
          other.role === entry.role &&
          isSameEntryDeliverable(other, entry),
      );

      // Skip entries already classified as "Other Actuals"
      if (isInOtherActuals) return totals;

      const hours = entry?.hours || 0;
      const cost = entry?.cost || 0;

      return {
        hours: totals.hours + hours,
        cost: totals.cost + cost,
      };
    },
    { hours: 0, cost: 0 },
  );
  const combinedExpenseSummary = {
    hours: totalExpenseSummary.hours + otherActualsSummary.hours,
    cost: totalExpenseSummary.cost + otherActualsSummary.cost,
  };

  const totalPercentage =
    totalBudgetCost > 0
      ? ((combinedExpenseSummary.cost / totalBudgetCost) * 100).toFixed(2)
      : '0.0';

  if (isProjectLoading || isUsersLoading) {
    return <Loader absolute />;
  }
  // helper to group tracked time by role for a deliverable
  const getActualRolesForDeliverable = (deliverable) => {
    const entries = expenseData.filter((e) => isEntryForDeliverable(e, deliverable));
    const grouped = entries.reduce((acc, e) => {
      const role = e.role || 'Unknown Role';
      if (!acc[role]) acc[role] = { hours: 0, cost: 0 };
      acc[role].hours += e.hours || 0;
      acc[role].cost += e.cost || 0;
      return acc;
    }, {});
    return Object.entries(grouped).map(([role, { hours, cost }]) => ({
      role,
      hours,
      cost,
    }));
  };
  return (
    <ProjectFinancialsStyled>
      <Typography variant="h6" sx={{ marginBottom: '8px' }}>
        Project Financials
      </Typography>
      <div className="prog-wrap">
        <ProgressBar
          value={totalPercentage}
          startLabel={'Project Totals (act. / est.)'}
          middleLabel={`$ ${formatNumber(combinedExpenseSummary.cost)} / $ ${formatNumber(
            totalBudgetCost,
          )}`}
          endLabel={
            plannerType === 'Fixed Fee Project'
              ? `${((combinedExpenseSummary.cost / totalBudgetCost) * 100).toFixed(2)}%`
              : `${formatNumber(combinedExpenseSummary.hours)} hrs (act.) (${(
                  (combinedExpenseSummary.cost / totalBudgetCost) *
                  100
                ).toFixed(2)}%)`
          }
        />
      </div>

      <div className="accordian-padding">
        {plannerType === 'Fixed Fee Project'
          ? // ─── Fixed Fee Project: show actuals only ───
            deliverables?.map((d) => {
              const { milestoneCostSpent, milestoneHoursSpent } = (() => {
                const { milestoneCostSpent, milestoneHoursSpent } = expenseData
                  .filter((e) => isEntryForDeliverable(e, d))
                  .reduce(
                    (t, e) => ({
                      milestoneCostSpent: t.milestoneCostSpent + (e.cost || 0),
                      milestoneHoursSpent: t.milestoneHoursSpent + (e.hours || 0),
                    }),
                    { milestoneCostSpent: 0, milestoneHoursSpent: 0 },
                  );
                return { milestoneCostSpent, milestoneHoursSpent };
              })();
              const roles = getActualRolesForDeliverable(d);
              return (
                <Accordian
                  key={d.id}
                  name={d.name}
                  expanded={expanded}
                  handleChange={setExpanded}
                  arrowPosition="right"
                  heading={
                    <ProgressBar
                      value={100}
                      startLabel={d.name}
                      middleLabel={`$ ${formatNumber(milestoneCostSpent)}`}
                      endLabel={`${formatNumber(milestoneHoursSpent)} hrs`}
                    />
                  }
                  body={
                    <div className="accordian-body">
                      {roles.map((r) => (
                        <DeliverableProgressWithLabel
                          key={r.role}
                          className="deliverable"
                          progressContent={
                            <ProgressBar
                              value={100}
                              startLabel={r.role}
                              middleLabel={`$ ${formatNumber(r.cost)}`}
                              endLabel={`${formatNumber(r.hours)} hrs`}
                            />
                          }
                        />
                      ))}
                    </div>
                  }
                />
              );
            })
          : plannerType === 'Fixed Fee Deliverable'
            ? // ─── Fixed Fee Deliverable: flat act/est bars ───
              deliverables.map((d) => {
                const estCost = d.budget || 0;
                const { milestoneCostSpent, milestoneHoursSpent } = (() => {
                  const { milestoneCostSpent, milestoneHoursSpent } = expenseData
                    .filter((e) => isEntryForDeliverable(e, d))
                    .reduce(
                      (t, e) => ({
                        milestoneCostSpent: t.milestoneCostSpent + (e.cost || 0),
                        milestoneHoursSpent: t.milestoneHoursSpent + (e.hours || 0),
                      }),
                      { milestoneCostSpent: 0, milestoneHoursSpent: 0 },
                    );
                  return { milestoneCostSpent, milestoneHoursSpent };
                })();
                const pct =
                  estCost > 0 ? ((milestoneCostSpent / estCost) * 100).toFixed(2) : '0.00';
                const roles = getActualRolesForDeliverable(d);
                return (
                  <Accordian
                    key={d.id}
                    name={d.name}
                    expanded={expanded}
                    handleChange={setExpanded}
                    arrowPosition="right"
                    heading={
                      <ProgressBar
                        value={pct}
                        startLabel={`${d.name} (act. / est.)`}
                        middleLabel={`$ ${formatNumber(milestoneCostSpent)} / $ ${formatNumber(
                          estCost,
                        )}`}
                        endLabel={`${formatNumber(milestoneHoursSpent)}  hrs (act.) (${pct}%)`}
                      />
                    }
                    body={
                      <div className="accordian-body">
                        {roles.map((r) => (
                          <DeliverableProgressWithLabel
                            key={r.role}
                            className="deliverable"
                            progressContent={
                              <ProgressBar
                                value={100}
                                startLabel={r.role}
                                middleLabel={`$ ${formatNumber(r.cost)}`}
                                endLabel={`${formatNumber(r.hours)} hrs`}
                              />
                            }
                          />
                        ))}
                      </div>
                    }
                  />
                );
              })
            : // ─── Time + Materials: unchanged collapsible accordions ───
              deliverables?.map((deliverable) => {
                const name = deliverable.name;
                const { milestoneCostSpent, milestoneHoursSpent } = getMilestoneTotals(deliverable);
                const { hours: milestoneBudgetedHours, value: milestoneBudgetedCost } =
                  calculateBudgetForDeliverable(deliverable, resources);
                const milestonePercentage = (milestoneCostSpent / milestoneBudgetedCost) * 100;

                return (
                  <Accordian
                    key={deliverable.id}
                    name={name}
                    handleChange={setExpanded}
                    expanded={expanded}
                    arrowPosition="right"
                    heading={
                      <div style={{ width: '100%' }}>
                        <ProgressBar
                          value={milestonePercentage}
                          startLabel={`${name} (act. / est.)`}
                          middleLabel={`$ ${formatNumber(milestoneCostSpent)} / $ ${formatNumber(
                            milestoneBudgetedCost,
                          )}`}
                          endLabel={`${formatNumber(milestoneHoursSpent)} / ${formatNumber(
                            milestoneBudgetedHours,
                          )} hrs (${formatNumber(milestonePercentage)}%)`}
                        />
                      </div>
                    }
                    body={
                      <div className="accordian-body">
                        {(() => {
                          const getDefaultRate = (role) => fetchedRolesList?.[role]?.rate || 0;

                          const deliverableExpenses = expenseDataWithRoleAndDeliverable?.filter(
                            (x) => isEntryForDeliverable(x, deliverable),
                          );

                          const aggregatedExpenses = deliverableExpenses?.reduce((acc, entry) => {
                            const role = entry.role || 'Unknown Role';
                            if (!acc[role]) acc[role] = { hours: 0, cost: 0 };
                            acc[role].hours += entry.hours || 0;
                            acc[role].cost += entry.cost || 0;
                            return acc;
                          }, {});

                          const estimatedRoles = getRolesListForDeliverable(deliverable, resources);

                          return estimatedRoles.map((role) => {
                            const actuals = aggregatedExpenses[role.role] || {
                              hours: 0,
                              cost: 0,
                            };
                            const expenseHours = actuals.hours;
                            const expenseCost =
                              actuals.cost || expenseHours * getDefaultRate(role.role);
                            const percentageSpent = role.hours
                              ? ((expenseHours / role.hours) * 100).toFixed(1)
                              : 0;

                            return (
                              <DeliverableProgressWithLabel
                                key={role.id}
                                progressContent={
                                  <ProgressBar
                                    value={percentageSpent}
                                    startLabel={role.role}
                                    middleLabel={`$ ${formatNumber(expenseCost)} / $ ${formatNumber(
                                      role.value,
                                    )}`}
                                    endLabel={`${formatNumber(expenseHours)} / ${formatNumber(
                                      role.hours,
                                    )} hrs (${percentageSpent}%)`}
                                  />
                                }
                                className="deliverable"
                              />
                            );
                          });
                        })()}
                      </div>
                    }
                  />
                );
              })}

        {/* “Other Actuals” only for Time + Materials */}
        {(!plannerType || plannerType === 'Time + Materials') && otherActualsSummary.hours > 0 && (
          <Accordian
            name="Other Actuals"
            handleChange={setExpanded}
            expanded={expanded}
            arrowPosition="right"
            heading={
              <ProgressBar
                value={100}
                startLabel="Other Actuals (act)"
                middleLabel={`$ ${formatNumber(otherActualsSummary.cost)}`}
                endLabel={`${formatNumber(otherActualsSummary.hours)} hrs`}
                description="Time tracked without budgeted roles."
              />
            }
            body={
              <div className="accordian-body">
                {otherActualsBreakdown.map((entry) => (
                  <DeliverableProgressWithLabel
                    key={entry.roleName}
                    progressContent={
                      <ProgressBar
                        value={100}
                        startLabel={entry.roleName}
                        middleLabel={`$ ${formatNumber(entry.cost)}`}
                        endLabel={`${entry.hours.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })} hrs`}
                      />
                    }
                    className="deliverable"
                  />
                ))}
              </div>
            }
          />
        )}
      </div>
    </ProjectFinancialsStyled>
  );
};

export default SingleProjectFinancials;
